import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Building2, CheckCircle2, ChevronRight, ClipboardList, FileText, HelpCircle, ListChecks, Loader2, Lock, Mail, MessageCircle, MessageSquare, Receipt, Star, Wrench, X } from "lucide-react";
import { STATES_BY_COUNTRY } from "@/lib/geoData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const textInput = "w-full rounded-full border border-slate-200 px-5 py-3 text-sm text-slate-900 transition-all focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-50";
const areaInput = "w-full rounded-2xl border border-slate-200 px-5 py-3 text-sm text-slate-900 transition-all focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-50";
const selectInput = `${textInput} appearance-none bg-white`;
const primaryBtn = "flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-4 text-sm font-bold text-white shadow-md transition-all hover:bg-blue-700 hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100";
const secondaryBtn = "w-full rounded-full border-2 border-slate-100 bg-white px-6 py-3.5 text-sm font-bold text-slate-600 transition-all hover:bg-slate-50 active:scale-[0.98] disabled:opacity-50";

const FieldInput = ({ field, value, onChange, file, onFile }) => {
  if (field.type === "file") {
    return <input type="file" onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid={`public-intake-field-${field.field_id}`} className="w-full rounded-2xl border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-600" />;
  }
  if (field.type === "state") {
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`public-intake-field-${field.field_id}`} className={selectInput}>
        <option value="">Select a state…</option>
        {STATES_BY_COUNTRY.US.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    );
  }
  if (field.type === "long_text") {
    return <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} rows={3} data-testid={`public-intake-field-${field.field_id}`} className={areaInput} />;
  }
  if (field.type === "dropdown") {
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`public-intake-field-${field.field_id}`} className={selectInput}>
        <option value="">Select…</option>
        {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === "multiple_choice") {
    return (
      <div className="mt-1 space-y-2" data-testid={`public-intake-field-${field.field_id}`}>
        {(field.options || []).map((o) => (
          <label key={o} className={`flex items-center gap-2.5 rounded-2xl border px-4 py-3 text-sm font-semibold transition-colors ${value === o ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}>
            <input type="radio" name={field.field_id} checked={value === o} onChange={() => onChange(o)} className="accent-blue-600" /> {o}
          </label>
        ))}
      </div>
    );
  }
  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-2.5 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600" data-testid={`public-intake-field-${field.field_id}`}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-blue-600" /> {field.placeholder || "Yes"}
      </label>
    );
  }
  const htmlType = field.type === "email" ? "email" : field.type === "number" ? "number" : field.type === "date" ? "date" : "text";
  return <input type={htmlType} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} data-testid={`public-intake-field-${field.field_id}`} className={textInput} />;
};

const OtpInput = ({ value, onChange, compact }) => {
  const refs = useRef([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] || "");

  const handleChange = (i, raw) => {
    const clean = raw.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = clean;
    onChange(next.join(""));
    if (clean && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key !== "Backspace") return;
    if (digits[i]) {
      e.preventDefault();
      const next = [...digits];
      next[i] = "";
      onChange(next.join(""));
      return;
    }
    if (i > 0) {
      e.preventDefault();
      const next = [...digits];
      next[i - 1] = "";
      onChange(next.join(""));
      refs.current[i - 1]?.focus();
    }
  };

  const boxClass = compact
    ? "h-12 w-10 text-xl"
    : "h-14 w-12 text-2xl sm:h-16 sm:w-14 sm:text-2xl";

  return (
    <div className={`mb-6 flex justify-center ${compact ? "gap-2" : "gap-2 sm:gap-3"}`} data-testid="public-intake-code-input">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          value={d}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          inputMode="numeric"
          maxLength={1}
          data-testid={`public-intake-code-input-${i}`}
          className={`${boxClass} rounded-xl border border-slate-200 bg-slate-50 text-center font-bold text-slate-900 transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-50`}
        />
      ))}
    </div>
  );
};

const BrandIcon = ({ variant, logoUrl, orgName }) => {
  if (variant === "success") {
    return (
      <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
        <CheckCircle2 className="h-11 w-11 text-emerald-500" />
      </div>
    );
  }
  if (variant === "error") {
    return (
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
        <AlertTriangle className="h-8 w-8 text-slate-400" />
      </div>
    );
  }
  if (variant === "verify") {
    return (
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-600 shadow-md shadow-blue-200">
        <MessageSquare className="h-7 w-7 text-white" />
      </div>
    );
  }
  return (
    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 p-[2px]">
      <div className="flex h-full w-full items-center justify-center rounded-full bg-white">
        {logoUrl ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} alt={orgName} className="h-full w-full rounded-full object-contain p-2" />
        ) : (
          <Building2 className="h-7 w-7 text-blue-500" />
        )}
      </div>
    </div>
  );
};

const StepPill = ({ children }) => (
  <div className="mb-5 flex justify-center">
    <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-600">{children}</span>
  </div>
);

const PURPOSE_ICON = {
  "Service Request": { Icon: Wrench, bg: "bg-blue-50", color: "text-blue-600" },
  "Estimate Request": { Icon: FileText, bg: "bg-purple-50", color: "text-purple-600" },
  "Consultation Request": { Icon: MessageCircle, bg: "bg-teal-50", color: "text-teal-600" },
  "General Inquiry": { Icon: HelpCircle, bg: "bg-emerald-50", color: "text-emerald-600" },
};
const DEFAULT_PURPOSE_ICON = { Icon: ClipboardList, bg: "bg-slate-100", color: "text-slate-600" };

const ModelCard = ({ model, onClick, disabled }) => {
  const { Icon, bg, color } = PURPOSE_ICON[model.purpose] || DEFAULT_PURPOSE_ICON;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={`public-intake-model-card-${model.model_id}`}
      className="flex w-full items-center gap-3.5 rounded-[20px] border border-slate-100 bg-white p-4 text-left shadow-sm transition-all hover:border-blue-200 hover:shadow-md active:scale-[0.99] disabled:opacity-50"
    >
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${bg}`}>
        <Icon size={20} className={color} />
      </span>
      <span className="min-w-0 flex-1">
        <p className="text-sm font-bold text-slate-900">{model.name}</p>
        {model.description && <p className="mt-0.5 truncate text-xs text-slate-500">{model.description}</p>}
      </span>
      <ChevronRight size={18} className="shrink-0 text-slate-300" />
    </button>
  );
};

const PortalActionCard = ({ icon: Icon, title, subtitle, onClick, testId, signedIn }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="flex w-full items-center gap-3.5 rounded-[20px] border border-slate-100 bg-white p-4 text-left shadow-sm transition-all hover:border-blue-200 hover:shadow-md active:scale-[0.99]"
  >
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100">
      <Icon size={20} className="text-slate-600" />
    </span>
    <span className="min-w-0 flex-1">
      <p className="text-sm font-bold text-slate-900">{title}</p>
      <p className="mt-0.5 truncate text-xs text-slate-500">{subtitle}</p>
    </span>
    {signedIn ? (
      <ChevronRight size={18} className="shrink-0 text-slate-300" />
    ) : (
      <span className="flex shrink-0 items-center gap-1 rounded-full bg-slate-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
        <Lock size={10} /> Sign in
      </span>
    )}
  </button>
);

const SECTION_META = {
  invoices: { title: "Invoices & Payments", blurb: "Your invoices and payment history will be listed here." },
  feedback: { title: "Feedback", blurb: "You'll be able to rate completed jobs here." },
};

export default function PublicIntakePage() {
  const { code } = useParams();
  const [stage, setStage] = useState("loading"); // loading | error | chooser | form | verify | submitting | success
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const [fieldError, setFieldError] = useState("");
  const [selecting, setSelecting] = useState(false);

  const [auth, setAuth] = useState(null);
  const [activePanel, setActivePanel] = useState(null); // null | "signin" | "my_requests" | "invoices" | "feedback"
  const [pendingSection, setPendingSection] = useState(null);
  const [authMethod, setAuthMethod] = useState("email");
  const [authContact, setAuthContact] = useState("");
  const [authStage, setAuthStage] = useState("contact"); // contact | code
  const [authChallengeId, setAuthChallengeId] = useState(null);
  const [authMasked, setAuthMasked] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [authRemember, setAuthRemember] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");
  const [myRequests, setMyRequests] = useState(null);
  const [myRequestsLoading, setMyRequestsLoading] = useState(false);
  const [myRequestsError, setMyRequestsError] = useState("");
  const portalDialogRef = useRef(null);
  useEffect(() => { if (activePanel) portalDialogRef.current?.focus(); }, [activePanel]);

  const [method, setMethod] = useState(null);
  const [contact, setContact] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [maskedContact, setMaskedContact] = useState("");
  const [code2, setCode2] = useState("");
  const [otpError, setOtpError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/public/intake/${code}`)
      .then((res) => { setData(res.data); setStage("chooser"); })
      .catch((err) => { setLoadError(err.response?.data?.detail || "This intake link is no longer available."); setStage("error"); });
    axios.get(`${API}/public/portal/${code}/auth/me`, { withCredentials: true })
      .then((res) => setAuth(res.data))
      .catch(() => setAuth({ authenticated: false }));
  }, [code]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (activePanel !== "my_requests") return;
    setMyRequestsLoading(true);
    setMyRequestsError("");
    axios.get(`${API}/public/portal/${code}/requests`, { withCredentials: true })
      .then((res) => setMyRequests(res.data.requests))
      .catch((err) => {
        if (err.response?.status === 401) {
          setAuth({ authenticated: false });
          setPendingSection("my_requests");
          setAuthStage("contact");
          setActivePanel("signin");
          return;
        }
        setMyRequestsError("Couldn't load your requests. Please try again.");
      })
      .finally(() => setMyRequestsLoading(false));
  }, [activePanel, code]);

  const openAccountCard = (section) => {
    if (auth?.authenticated) { setActivePanel(section); return; }
    setPendingSection(section);
    setAuthStage("contact");
    setAuthContact("");
    setAuthCode("");
    setAuthError("");
    setAuthRemember(false);
    setActivePanel("signin");
  };

  const requestPortalOtp = async () => {
    setAuthError("");
    setAuthBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/public/portal/${code}/auth/otp/request`, { contact: authContact, method: authMethod });
      setAuthChallengeId(res.challenge_id);
      setAuthMasked(res.contact_masked);
      setAuthStage("code");
    } catch (err) {
      if (err.response?.status === 503 && err.response?.data?.detail === "sms_unavailable") {
        setAuthError("SMS verification is temporarily unavailable. Please use Email instead.");
      } else {
        setAuthError(err.response?.data?.detail || "Couldn't send a code. Please try again.");
      }
    } finally {
      setAuthBusy(false);
    }
  };

  const verifyPortalOtp = async () => {
    setAuthError("");
    setAuthBusy(true);
    try {
      const { data: res } = await axios.post(
        `${API}/public/portal/${code}/auth/otp/verify`,
        { challenge_id: authChallengeId, code: authCode, remember: authRemember },
        { withCredentials: true }
      );
      setAuth({ authenticated: true, customer_name: res.customer_name, contact_masked: res.contact_masked, remember: authRemember });
      setActivePanel(pendingSection);
    } catch (err) {
      setAuthError(err.response?.data?.detail || "Incorrect code. Please try again.");
    } finally {
      setAuthBusy(false);
    }
  };

  const portalLogout = async () => {
    try {
      await axios.post(`${API}/public/portal/${code}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    setAuth({ authenticated: false });
    setActivePanel(null);
    setMyRequests(null);
  };

  const selectModel = async (modelId) => {
    setSelecting(true);
    setFieldError("");
    try {
      const { data: res } = await axios.post(`${API}/public/intake/select-model`, { code: data.code, model_id: modelId });
      setData((prev) => ({ ...prev, challenge_id: res.challenge_id, sms_otp_available: res.sms_otp_available, model: res.model }));
      setValues({});
      setFiles({});
      setStage("form");
    } catch (err) {
      setFieldError(err.response?.data?.detail || "This option is no longer available.");
    } finally {
      setSelecting(false);
    }
  };

  const setValue = (fieldId, v) => setValues((prev) => ({ ...prev, [fieldId]: v }));
  const setFile = (fieldId, f) => setFiles((prev) => ({ ...prev, [fieldId]: f }));

  const fieldByMapping = (mapping) => data?.model?.fields?.find((f) => f.canonical_mapping === mapping);
  const contactForMethod = (m) => {
    const f = fieldByMapping(m === "email" ? "email" : "phone");
    return f ? (values[f.field_id] || "") : "";
  };

  const continueToVerify = () => {
    for (const f of data.model.fields) {
      if (f.type === "file") {
        if (f.required && !files[f.field_id]) { setFieldError(`"${f.label}" is required`); return; }
        continue;
      }
      const v = values[f.field_id];
      if (f.required && (v === undefined || v === null || v === "" || v === false)) {
        setFieldError(`"${f.label}" is required`);
        return;
      }
    }
    setFieldError("");
    const methodsEnabled = data.model.identity_methods.filter((m) => m !== "sms" || data.sms_otp_available);
    const methodsWithContact = methodsEnabled.filter((m) => !!contactForMethod(m));
    const chosen = methodsWithContact[0] || methodsEnabled[0] || null;
    setMethod(chosen);
    setContact(chosen ? contactForMethod(chosen) : "");
    setStage("verify");
  };

  const requestOtp = async () => {
    setOtpError("");
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/public/intake/otp/request`, { challenge_id: data.challenge_id, method, contact });
      setOtpSent(true);
      setMaskedContact(res.contact_masked);
    } catch (err) {
      if (err.response?.status === 503 && err.response?.data?.detail === "sms_unavailable") {
        setOtpError("SMS verification is temporarily unavailable. Please use Email instead.");
      } else {
        setOtpError(err.response?.data?.detail || "Couldn't send a code. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const submitAll = useCallback(async () => {
    setStage("submitting");
    try {
      const formData = new FormData();
      formData.append("challenge_id", data.challenge_id);
      formData.append("values_json", JSON.stringify(values));
      Object.entries(files).forEach(([fieldId, file]) => { if (file) formData.append(`file__${fieldId}`, file); });
      const { data: res } = await axios.post(`${API}/public/intake/submit`, formData);
      setResult(res);
      setStage("success");
    } catch (err) {
      setOtpError(err.response?.data?.detail || "Something went wrong submitting your request.");
      setStage("verify");
    }
  }, [data, values, files]);

  const verifyOtp = async () => {
    setOtpError("");
    setBusy(true);
    try {
      await axios.post(`${API}/public/intake/otp/verify`, { challenge_id: data.challenge_id, code: code2 });
      await submitAll();
    } catch (err) {
      setOtpError(err.response?.data?.detail || "Incorrect code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const switchMethod = (m) => {
    setMethod(m);
    setOtpSent(false);
    setContact(contactForMethod(m));
    setCode2("");
    setOtpError("");
  };

  if (stage === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F9FC]">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" data-testid="public-intake-loading" />
      </div>
    );
  }

  if (stage === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F9FC] px-4">
        <div className="max-w-sm text-center" data-testid="public-intake-error">
          <BrandIcon variant="error" />
          <p className="text-lg font-bold text-slate-700">{loadError}</p>
        </div>
      </div>
    );
  }

  if (stage === "success") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F9FC] px-4 py-10" data-testid="public-intake-success">
        <div className="w-full max-w-md rounded-[24px] border border-slate-100 bg-white p-8 text-center shadow-sm">
          <BrandIcon variant="success" />
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Request Submitted!</h1>
          <p className="mt-2 text-sm text-slate-500">{result.confirmation_message}</p>
          <div className="mt-5 rounded-2xl bg-slate-50 px-4 py-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Reference</p>
            <p className="mt-0.5 text-sm font-bold text-slate-800" data-testid="public-intake-display-id">{result.display_id}</p>
          </div>
        </div>
      </div>
    );
  }

  if (stage === "chooser") {
    return (
      <div className="min-h-screen bg-[#F7F9FC] py-8 md:py-12" data-testid="public-intake-chooser">
        <div className="mx-auto max-w-md px-4">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex items-center justify-center gap-1.5">
              <img src="/nova-icon-official.png" alt="Nova Opsis" className="h-5 w-5" />
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Nova Opsis</span>
            </div>
            <div className="mx-auto mb-4 flex h-28 w-full max-w-[220px] items-center justify-center rounded-[28px] bg-gradient-to-br from-slate-50 to-slate-100 p-5 sm:h-32">
              {data.org_logo_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${data.org_logo_url}`} alt={data.org_name} className="h-full w-full object-contain" />
              ) : (
                <Building2 className="h-12 w-12 text-slate-300" />
              )}
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{data.org_name}</h1>
            <p className="mt-1 text-sm text-slate-500">How can we help you today?</p>
          </div>

          {data.models.length > 0 && (
            <div className="mb-3 px-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">New Request</div>
          )}
          <div className="space-y-3" data-testid="public-intake-model-list">
            {data.models.map((m) => (
              <ModelCard key={m.model_id} model={m} disabled={selecting} onClick={() => selectModel(m.model_id)} />
            ))}
          </div>
          {fieldError && <p className="mt-3 text-center text-xs font-semibold text-red-600" data-testid="public-intake-chooser-error">{fieldError}</p>}
          {selecting && (
            <div className="mt-4 flex justify-center" data-testid="public-intake-chooser-loading">
              <Loader2 size={18} className="animate-spin text-blue-500" />
            </div>
          )}

          {data.portal && (
            <>
              <div className="mb-3 mt-7 flex items-center justify-between px-1">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Your Account</span>
                {auth?.authenticated && (
                  <button type="button" onClick={portalLogout} data-testid="public-intake-portal-signout" className="text-[11px] font-bold text-blue-600 hover:text-blue-700">
                    Sign out
                  </button>
                )}
              </div>
              {auth?.authenticated && (
                <p className="mb-3 px-1 text-xs text-slate-500" data-testid="public-intake-portal-signed-in-as">
                  Signed in as <span className="font-bold text-slate-700">{auth.customer_name || auth.contact_masked}</span>
                </p>
              )}
              <div className="space-y-3" data-testid="public-intake-portal-list">
                {data.portal.my_requests_available && (
                  <PortalActionCard icon={ListChecks} title="My Requests" subtitle="Track requests you've submitted" onClick={() => openAccountCard("my_requests")} testId="public-intake-portal-my-requests" signedIn={auth?.authenticated} />
                )}
                {data.portal.invoices_available && (
                  <PortalActionCard icon={Receipt} title="Invoices & Payments" subtitle="View and pay your invoices" onClick={() => openAccountCard("invoices")} testId="public-intake-portal-invoices" signedIn={auth?.authenticated} />
                )}
                <PortalActionCard icon={Star} title="Feedback" subtitle="Rate a completed job" onClick={() => openAccountCard("feedback")} testId="public-intake-portal-feedback" signedIn={auth?.authenticated} />
              </div>
            </>
          )}

          {activePanel && (
            <div
              className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/30 p-4 sm:items-center"
              onClick={() => setActivePanel(null)}
              data-testid="public-intake-portal-notice"
              role="dialog"
              aria-modal="true"
              onKeyDown={(e) => e.key === "Escape" && setActivePanel(null)}
              tabIndex={-1}
              ref={portalDialogRef}
            >
              <div className={`relative w-full rounded-[24px] bg-white p-6 shadow-xl ${activePanel === "my_requests" ? "max-w-md" : "max-w-[22rem]"}`} onClick={(e) => e.stopPropagation()}>
                <button type="button" onClick={() => setActivePanel(null)} data-testid="public-intake-portal-notice-close" className="absolute right-4 top-4 text-slate-300 hover:text-slate-500"><X size={18} /></button>

                {activePanel === "signin" && (
                  <div className="text-center" data-testid="public-intake-signin-panel">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50"><Lock size={20} className="text-blue-500" /></div>
                    <p className="text-sm font-bold text-slate-900">Sign in to your account</p>
                    <p className="mt-1 mb-5 text-xs text-slate-500">We'll text or email you a one-time code — no password needed.</p>

                    {authStage === "contact" ? (
                      <>
                        {data.portal?.sms_otp_available && (
                          <div className="mb-4 flex gap-2.5">
                            {["email", "sms"].map((m) => (
                              <button
                                key={m}
                                type="button"
                                onClick={() => setAuthMethod(m)}
                                data-testid={`public-intake-portal-auth-method-${m}`}
                                className={`flex-1 rounded-2xl border-2 px-3 py-3 text-xs font-bold transition-colors ${authMethod === m ? "border-blue-500 bg-blue-50 text-blue-600" : "border-slate-100 text-slate-500"}`}
                              >
                                {m === "email" ? <Mail size={16} className="mx-auto mb-1" /> : <MessageSquare size={16} className="mx-auto mb-1" />}
                                {m === "email" ? "Email" : "SMS"}
                              </button>
                            ))}
                          </div>
                        )}
                        <input
                          value={authContact}
                          onChange={(e) => setAuthContact(e.target.value)}
                          placeholder={authMethod === "email" ? "you@example.com" : "(555) 123-4567"}
                          data-testid="public-intake-portal-auth-contact-input"
                          className={`mb-4 ${textInput}`}
                        />
                        {authError && <p className="mb-4 text-xs font-semibold text-red-600" data-testid="public-intake-portal-auth-error">{authError}</p>}
                        <button type="button" disabled={authBusy || !authContact} onClick={requestPortalOtp} data-testid="public-intake-portal-send-code-btn" className={primaryBtn}>
                          {authBusy && <Loader2 size={14} className="animate-spin" />} Send Code
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="mb-5 text-center text-sm text-slate-500">We sent a code to <span className="font-bold text-slate-800">{authMasked}</span></p>
                        <OtpInput value={authCode} onChange={setAuthCode} compact />
                        <label className="mb-4 flex items-center justify-center gap-2 text-xs font-semibold text-slate-500" data-testid="public-intake-portal-remember-label">
                          <input type="checkbox" checked={authRemember} onChange={(e) => setAuthRemember(e.target.checked)} className="h-4 w-4 accent-blue-600" data-testid="public-intake-portal-remember-checkbox" />
                          Keep me signed in for 30 days
                        </label>
                        {authError && <p className="mb-4 text-center text-xs font-semibold text-red-600" data-testid="public-intake-portal-auth-error">{authError}</p>}
                        <button type="button" disabled={authBusy || authCode.length !== 6} onClick={verifyPortalOtp} data-testid="public-intake-portal-verify-btn" className={`mb-3 ${primaryBtn}`}>
                          {authBusy && <Loader2 size={14} className="animate-spin" />} Verify &amp; Sign In
                        </button>
                        <button type="button" onClick={requestPortalOtp} disabled={authBusy} data-testid="public-intake-portal-resend-btn" className={secondaryBtn}>
                          Resend Code
                        </button>
                      </>
                    )}
                  </div>
                )}

                {activePanel === "my_requests" && (
                  <div data-testid="public-intake-my-requests-panel">
                    <p className="mb-4 text-sm font-bold text-slate-900">My Requests</p>
                    {myRequestsLoading && (
                      <div className="flex justify-center py-6" data-testid="public-intake-my-requests-loading"><Loader2 size={18} className="animate-spin text-blue-500" /></div>
                    )}
                    {!myRequestsLoading && myRequestsError && (
                      <p className="py-4 text-center text-xs font-semibold text-red-600" data-testid="public-intake-my-requests-error">{myRequestsError}</p>
                    )}
                    {!myRequestsLoading && !myRequestsError && myRequests && myRequests.length === 0 && (
                      <div className="py-6 text-center" data-testid="public-intake-my-requests-empty">
                        <ClipboardList size={28} className="mx-auto mb-2 text-slate-300" />
                        <p className="text-xs text-slate-500">You haven't submitted any requests yet.</p>
                      </div>
                    )}
                    {!myRequestsLoading && !myRequestsError && myRequests && myRequests.length > 0 && (
                      <div className="max-h-[60vh] space-y-2.5 overflow-y-auto pr-1" data-testid="public-intake-my-requests-list">
                        {myRequests.map((r) => (
                          <div key={r.submission_id} data-testid={`public-intake-my-requests-item-${r.submission_id}`} className="rounded-2xl border border-slate-100 bg-slate-50 p-3.5 text-left">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm font-bold text-slate-900">{r.service_name}</p>
                              <span className="shrink-0 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-bold text-slate-500">{r.status_label}</span>
                            </div>
                            {r.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{r.description}</p>}
                            <p className="mt-1.5 text-[11px] text-slate-400">{r.display_id} · {new Date(r.submitted_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activePanel !== "signin" && activePanel !== "my_requests" && SECTION_META[activePanel] && (
                  <div className="text-center" data-testid={`public-intake-portal-section-${activePanel}`}>
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50"><CheckCircle2 size={20} className="text-emerald-500" /></div>
                    <p className="text-sm font-bold text-slate-900">{SECTION_META[activePanel].title}</p>
                    <p className="mt-1 text-xs text-slate-500">{SECTION_META[activePanel].blurb}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <p className="mt-8 text-center text-[11px] font-semibold text-slate-400" data-testid="public-intake-portal-footer">Secure customer access · Powered by Nova Opsis</p>
        </div>

      </div>
    );
  }

  const methodsEnabled = data.model.identity_methods.filter((m) => m !== "sms" || data.sms_otp_available);
  const methodsWithContact = methodsEnabled.filter((m) => !!contactForMethod(m));
  const availableMethods = methodsWithContact.length > 0 ? methodsWithContact : methodsEnabled;
  const autoFilled = methodsWithContact.length > 0;
  const blockedBySms = data.model.identity_methods.length > 0 && availableMethods.length === 0;

  return (
    <div className="min-h-screen bg-[#F7F9FC] py-8 md:py-12" data-testid="public-intake-page">
      <div className="mx-auto max-w-md px-4">
        <BrandIcon variant={stage === "verify" ? "verify" : "form"} logoUrl={data.org_logo_url} orgName={data.org_name} />

        <div className="mb-6 text-center">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{data.org_name}</p>
          <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{stage === "verify" ? "Check your contact" : data.model.name}</h1>
          {stage === "form" && data.model.description && <p className="mt-2 text-sm text-slate-500">{data.model.description}</p>}
        </div>

        {stage === "form" && (
          <>
            <button type="button" onClick={() => setStage("chooser")} data-testid="public-intake-back-to-chooser-btn" className="mb-3 text-xs font-bold text-slate-400 hover:text-slate-600">
              ← Back to services
            </button>
            <StepPill>Step 1 of 2 · Details</StepPill>
            <div className="space-y-5 rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm sm:p-8" data-testid="public-intake-form-card">
              {data.model.fields.map((f) => (
                <div key={f.field_id}>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
                    {f.label} {f.required && <span className="text-red-500">*</span>}
                  </label>
                  {f.description && <p className="mb-1.5 text-xs text-slate-400">{f.description}</p>}
                  <FieldInput field={f} value={values[f.field_id]} onChange={(v) => setValue(f.field_id, v)} file={files[f.field_id]} onFile={(file) => setFile(f.field_id, file)} />
                </div>
              ))}
              {fieldError && <p className="text-xs font-semibold text-red-600" data-testid="public-intake-field-error">{fieldError}</p>}
              <button type="button" onClick={continueToVerify} data-testid="public-intake-continue-btn" className={primaryBtn}>
                Continue
              </button>
            </div>
          </>
        )}

        {stage === "verify" && (
          <>
            <StepPill>Step 2 of 2 · Verify</StepPill>
            <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm sm:p-8" data-testid="public-intake-verify-card">
              {blockedBySms ? (
                <p className="text-sm text-red-600" data-testid="public-intake-verify-blocked">Verification is temporarily unavailable. Please contact the business directly.</p>
              ) : !otpSent ? (
                <>
                  {availableMethods.length > 1 && (
                    <div className="mb-4 flex gap-2.5">
                      {availableMethods.map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => switchMethod(m)}
                          data-testid={`public-intake-method-${m}`}
                          className={`flex-1 rounded-2xl border-2 px-3 py-3 text-xs font-bold transition-colors ${method === m ? "border-blue-500 bg-blue-50 text-blue-600" : "border-slate-100 text-slate-500"}`}
                        >
                          {m === "email" ? <Mail size={16} className="mx-auto mb-1" /> : <MessageSquare size={16} className="mx-auto mb-1" />}
                          {m === "email" ? "Email" : "SMS"}
                        </button>
                      ))}
                    </div>
                  )}
                  {autoFilled ? (
                    <p className="mb-5 text-center text-sm text-slate-600" data-testid="public-intake-contact-display">
                      We'll send a verification code to<br /><span className="font-bold text-slate-900">{contact}</span>
                    </p>
                  ) : (
                    <input
                      value={contact}
                      onChange={(e) => setContact(e.target.value)}
                      placeholder={method === "email" ? "you@example.com" : "(555) 123-4567"}
                      data-testid="public-intake-contact-input"
                      className={`mb-5 ${textInput}`}
                    />
                  )}
                  {otpError && <p className="mb-4 text-xs font-semibold text-red-600" data-testid="public-intake-otp-error">{otpError}</p>}
                  <button type="button" disabled={busy || !contact} onClick={requestOtp} data-testid="public-intake-send-code-btn" className={primaryBtn}>
                    {busy && <Loader2 size={14} className="animate-spin" />} Send Code
                  </button>
                </>
              ) : (
                <>
                  <p className="mb-5 text-center text-sm text-slate-500">We sent a code to <span className="font-bold text-slate-800">{maskedContact}</span></p>
                  <OtpInput value={code2} onChange={setCode2} />
                  {otpError && <p className="mb-4 text-center text-xs font-semibold text-red-600" data-testid="public-intake-otp-error">{otpError}</p>}
                  <button type="button" disabled={busy || code2.length !== 6} onClick={verifyOtp} data-testid="public-intake-verify-btn" className={`mb-3 ${primaryBtn}`}>
                    {busy && <Loader2 size={14} className="animate-spin" />} Verify &amp; Submit
                  </button>
                  <button type="button" onClick={requestOtp} disabled={busy} data-testid="public-intake-resend-btn" className={secondaryBtn}>
                    Resend Code
                  </button>
                </>
              )}
              <button type="button" onClick={() => setStage("form")} data-testid="public-intake-back-btn" className="mt-4 w-full text-center text-xs font-bold text-slate-400 hover:text-slate-600">
                ← Back to form
              </button>
            </div>
          </>
        )}

        {stage === "submitting" && (
          <div className="flex items-center justify-center rounded-[24px] border border-slate-100 bg-white p-12 shadow-sm" data-testid="public-intake-submitting">
            <Loader2 size={22} className="animate-spin text-blue-500" />
          </div>
        )}
      </div>
    </div>
  );
}
